import { NavLink } from "react-router-dom";
import {
  Banknote,
  Building2,
  FileText,
  LayoutDashboard,
  Receipt,
  Settings,
  Users,
  Wrench,
} from "lucide-react";
import type { VisaoGeral } from "@/api/contrato";
import { RodapeDoRelay } from "./RodapeDoRelay";

/*
 * Barra lateral FLUTUANTE, com raio próprio e margem do canto (doc 06a §3.4) —
 * não colada na borda da janela.
 *
 * Item ativo: fundo `--superficie-2` e um traço de 2px em `--esferografica` na
 * borda esquerda interna, por `box-shadow: inset` — e não fundo chapado
 * colorido, que competiria com a paleta de estados.
 */
interface Item {
  para: string;
  rotulo: string;
  Icone: typeof LayoutDashboard;
  /** Ainda não construída. A rodada desta change é a fundação. */
  pendente?: boolean;
}

const ITENS: Item[] = [
  { para: "/", rotulo: "Visão geral", Icone: LayoutDashboard },
  { para: "/boletos", rotulo: "Boletos", Icone: FileText },
  { para: "/operacao", rotulo: "Operação", Icone: Wrench },
  // Antes de Aplicações: é a ordem do fluxo, cedente, conta, convênio e aplicação.
  { para: "/cedentes", rotulo: "Cedentes", Icone: Users },
  { para: "/aplicacoes", rotulo: "Aplicações", Icone: Building2 },
  { para: "/planos", rotulo: "Planos", Icone: Receipt },
  { para: "/faturas", rotulo: "Faturas", Icone: Banknote },
  { para: "/configuracoes", rotulo: "Configurações", Icone: Settings, pendente: true },
];

export function BarraLateral({ visao }: { visao: VisaoGeral | undefined }) {
  const indeterminados =
    visao?.indeterminados?.estado === "ok" ? (visao.indeterminados.valor ?? 0) : 0;

  return (
    <nav
      aria-label="Seções do painel"
      className="m-2 flex w-[var(--largura-lateral)] shrink-0 flex-col rounded-cartao border border-fio bg-superficie"
    >
      <ul className="flex flex-col gap-[2px] p-2">
        {ITENS.map((item) => (
          <li key={item.para}>
            <NavLink
              to={item.para}
              end={item.para === "/"}
              className={({ isActive }) =>
                [
                  "flex h-8 items-center gap-2 rounded-controle px-2 text-corpo",
                  isActive
                    ? "bg-superficie-2 text-texto shadow-[inset_2px_0_0_var(--esferografica)]"
                    : "text-texto-2 hover:bg-superficie-2 hover:text-texto",
                ].join(" ")
              }
            >
              <item.Icone aria-hidden="true" className="size-4 shrink-0" />
              <span className="truncate">{item.rotulo}</span>

              {/*
                O contador de indeterminados é a pílula magenta SÓLIDA, e é o
                único ponto de cor saturada da barra lateral (doc 06a §4.1). Ele
                aparece na Visão geral porque é lá que a resolução acontece.
              */}
              {item.para === "/" && indeterminados > 0 && (
                <span className="dados ml-auto rounded-full bg-indeterminado px-1.5 text-[11px] font-medium text-breu">
                  {indeterminados}
                </span>
              )}

              {item.pendente === true && (
                <span className="rotulo ml-auto text-texto-3" title="Ainda não construída">
                  em breve
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <RodapeDoRelay visao={visao} />
      </div>
    </nav>
  );
}
